import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Calculator, Clock, FileText, Download } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { BreadcrumbItem } from "@/components/Breadcrumb";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { getDetailProfit } from "@/api/Admin/Profit";
import Spinner from "@/components/Spinner";
import { toast, ToastContainer } from "react-toastify"; // Added ToastContainer here just in case, though usually at root
import "react-toastify/dist/ReactToastify.css";

type PaymentStatus = "paid" | "pending" | "created";

interface PaymentHistory {
  period: string;
  paymentDate: string;
  amount: number;
  status: PaymentStatus;
}

interface PaymentDocument {
  id: string;
  fileName: string;
  uploadDate: string;
}

interface PaymentDetail {
  id: string;
  investorName: string;
  investorCode: string;
  packageName: string;
  investmentAmount: number;
  interestRate: number;
  interestAmount: number;
  taxRate: number;
  taxAmount: number;
  netAmount: number;
  paymentPeriod: string;
  daysInPeriod: number;
  status: PaymentStatus;
  paymentHistory: PaymentHistory[];
  documents: PaymentDocument[];
  accountingNote: string;
}

// Mock data - replace with API call
const mockPaymentDetail: PaymentDetail = {
  id: "1",
  investorName: "Nguyễn Văn An",
  investorCode: "BEQ001",
  packageName: "Gói Platinum 12 tháng",
  investmentAmount: 200000000,
  interestRate: 9.5,
  interestAmount: 1583333,
  taxRate: 5,
  taxAmount: 79167,
  netAmount: 1504166,
  paymentPeriod: "11/2024",
  daysInPeriod: 30,
  status: "paid",
  paymentHistory: [
    { period: "T1/2024", paymentDate: "15/2/2024", amount: 1583333, status: "paid" },
    { period: "T2/2024", paymentDate: "15/3/2024", amount: 1583333, status: "paid" },
    { period: "T3/2024", paymentDate: "15/4/2024", amount: 1583333, status: "paid" },
  ],
  documents: [
    { id: "1", fileName: "chung_tu_chi_tien_T11_2024.pdf", uploadDate: "15/11/2024" },
    { id: "2", fileName: "xac_nhan_ngan_hang.pdf", uploadDate: "15/11/2024" },
  ],
  accountingNote: "Đã chi trả và đối soát thành công với ngân hàng Vietcombank",
};

const InterestPaymentDetail: React.FC = () => {
  const { paymentId } = useParams<{ paymentId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { investorName, investorId, paymentPeriod } = location.state || {};

  const [payment, setPayment] = useState<PaymentDetail | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!paymentId || !paymentPeriod) return;
      setLoading(true);
      try {
        const res = await getDetailProfit({ id: Number(paymentId), period: paymentPeriod });
        if (res?.data) {
          const data = res.data;
          const info = data.package_info;
          const periodDetails = data.period_details;

          // Calculate tax rate if possible, else default 5
          const gross = Number(periodDetails.gross_amount);
          const tax = Number(periodDetails.tax_fee);
          const calculatedTaxRate = gross > 0 ? (tax / gross) * 100 : 5;

           // Determine status from period details transaction or fallback
           // Assuming if transaction exists and is success -> paid?? Or maybe periodDetails has status??
           // The API type provided has `period_details: { ... transaction: any | null }` and `history` array.
           // Let's use history to find status for current period if matches (though history array might be all history)
           // Or verify what `status` property in history represents.
           // mocked logic: use 'pending' unless found otherwise.
           let status: PaymentStatus = 'pending';
           // Check if current period exists in history and what its status is
           const currentPeriodHistory = data.history.find((h: any) => h.period === paymentPeriod);
           if (currentPeriodHistory) {
              const histStatus = currentPeriodHistory.status.toLowerCase();
              if (histStatus === 'paid' || histStatus === 'success') status = 'paid';
           }

          const mappedPayment: PaymentDetail = {
            id: paymentId,
            investorName: info.user_info.fullname,
            investorCode: info.user_info.cccd_number || `BEQ${info.user_info.id}`,
            packageName: info.package_name,
            investmentAmount: Number(info.price_snapshot),
            interestRate: Number(info.profit_rate),
            interestAmount: Number(periodDetails.gross_amount),
            taxRate: Math.round(calculatedTaxRate),
            taxAmount: Number(periodDetails.tax_fee),
            netAmount: Number(periodDetails.net_amount),
            paymentPeriod: paymentPeriod,
            daysInPeriod: 30, // Defaulting due to lack of distinct field
            status: status,
            paymentHistory: data.history.map((h: any) => ({
              period: h.period,
              paymentDate: h.due_date,
              amount: Number(h.net_amount),
              status: h.status.toLowerCase() === 'paid' ? 'paid' : 'pending' 
            })),
            documents: [], // Not provided in API response yet
            accountingNote: periodDetails.transaction?.description || periodDetails.transaction?.admin_note || ""
          };
          setPayment(mappedPayment);
        }
      } catch (error) {
        console.error("Error fetching detail:", error);
        toast.error(t("common.error"));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [paymentId, paymentPeriod, t]);

  const breadcrumbs: BreadcrumbItem[] = [
    { label: "Dashboard", path: "/admin/dashboard" },
    { label: t("interestPayment.title"), path: "/admin/interest-payments" },
    { label: t("interestPayment.detail") },
  ];

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString("vi-VN", { maximumFractionDigits: 0}) + " đ";
  };

  const getStatusBadge = (status: PaymentStatus) => {
    const badges = {
      paid: {
        bg: "bg-green-100 dark:bg-green-900/30",
        text: "text-green-700 dark:text-green-400",
        border: "border-green-300 dark:border-green-700",
        dot: "bg-green-500",
        label: t("interestPayment.statusPaid"),
      },
      created: {
        bg: "bg-yellow-100 dark:bg-yellow-900/30",
        text: "text-yellow-700 dark:text-yellow-400",
        border: "border-yellow-300 dark:border-yellow-700",
        dot: "bg-yellow-500",
        label: t("interestPayment.statusCreated"),
      },
      pending: {
        bg: "bg-gray-100 dark:bg-gray-700/30",
        text: "text-gray-700 dark:text-gray-400",
        border: "border-gray-300 dark:border-gray-600",
        dot: "bg-gray-500",
        label: t("interestPayment.statusPending"),
      },
    };
    const badge = badges[status];
    return (
      <span
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border ${badge.bg} ${badge.text} ${badge.border}`}
      >
        <span className={`w-2.5 h-2.5 rounded-full ${badge.dot}`}></span>
        {badge.label}
      </span>
    );
  };

  const getSmallStatusBadge = (status: PaymentStatus) => {
    const badges = {
      paid: {
        bg: "bg-green-100 dark:bg-green-900/30",
        text: "text-green-700 dark:text-green-400",
        label: t("interestPayment.paidShort"),
      },
      created: {
        bg: "bg-yellow-100 dark:bg-yellow-900/30",
        text: "text-yellow-700 dark:text-yellow-400",
        label: t("interestPayment.statusCreated"),
      },
      pending: {
        bg: "bg-gray-100 dark:bg-gray-700/30",
        text: "text-gray-700 dark:text-gray-400",
        label: t("interestPayment.statusPending"),
      },
    };
    const badge = badges[status];
    return (
      <span
        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}
      >
        {badge.label}
      </span>
    );
  };

  const handleDownload = (fileName: string) => {
    console.log("Download:", fileName);
  };

  if (loading) {
    return (
      <div className="dark:bg-dark-bg min-h-screen flex items-center justify-center">
        <Spinner size={40} />
      </div>
    );
  }

  if (!payment) {
    return (
       <div className="dark:bg-dark-bg min-h-screen p-6"> 
        <div className="text-center text-gray-500 dark:text-gray-400 mt-10">
            {t("common.noData")}
        </div>
       </div>
    );
  }

  const dailyRate = (payment.interestRate / 365).toFixed(4);

  return (
    <div className="dark:bg-dark-bg min-h-screen">
      <Helmet>
        <title>
          {t("interestPayment.detail")} - {t("interestPayment.month")} {payment.paymentPeriod}
        </title>
      </Helmet>

      {/* Header */}
      <PageHeader
        title={t("interestPayment.detailTitle")}
        breadcrumbs={breadcrumbs}
      />

      {/* Investor Info Section */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {payment.investorName} - {payment.investorCode}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t("interestPayment.month")} {payment.paymentPeriod}
          </p>
        </div>
        <div>{getStatusBadge(payment.status)}</div>
      </div>

      {/* Payment Info Card */}
      <div className="bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
          {t("interestPayment.paymentInfo")}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
              {t("interestPayment.packageName")}
            </p>
            <p className="text-base font-medium text-gray-900 dark:text-white">
              {payment.packageName}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
              {t("interestPayment.investmentAmount")}
            </p>
            <p className="text-base font-bold text-gray-900 dark:text-white">
              {formatCurrency(payment.investmentAmount)}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
              {t("interestPayment.interestRateYear")}
            </p>
            <p className="text-base font-bold text-green-600 dark:text-green-400">
              {payment.interestRate}%/{t("interestPayment.year")}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
              {t("interestPayment.interestToPay")}
            </p>
            <p className="text-xl font-bold text-gray-900 dark:text-white">
              {formatCurrency(payment.interestAmount)}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
              {t("interestPayment.taxDeduction")} ({payment.taxRate}%)
            </p>
            <p className="text-xl font-bold text-red-500 dark:text-red-400">
              {formatCurrency(payment.taxAmount)}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
              {t("interestPayment.netReceived")}
            </p>
            <p className="text-xl font-bold text-green-600 dark:text-green-400">
              {formatCurrency(payment.netAmount)}
            </p>
          </div>
        </div>
      </div>

      {/* Interest Calculation Card */}
      <div className="bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-md">
            <Calculator className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t("interestPayment.calculationFormula")}
          </h2>
        </div>

        <div className="space-y-0">
          <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-700">
            <span className="text-gray-600 dark:text-gray-400">
              {t("interestPayment.investmentAmount")}:
            </span>
            <span className="font-medium text-gray-900 dark:text-white">
              {formatCurrency(payment.investmentAmount)}
            </span>
          </div>

          <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-700">
            <span className="text-gray-600 dark:text-gray-400">
              {t("interestPayment.yearlyRate")}:
            </span>
            <span className="font-medium text-gray-900 dark:text-white">
              {payment.interestRate}%
            </span>
          </div>

          <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-700">
            <span className="text-gray-600 dark:text-gray-400">
              {t("interestPayment.dailyRate")}:
            </span>
            <span className="font-medium text-gray-900 dark:text-white">
              {dailyRate}%
            </span>
          </div>

          <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-700">
            <span className="text-gray-600 dark:text-gray-400">
              {t("interestPayment.daysCount")}:
            </span>
            <span className="font-medium text-gray-900 dark:text-white">
              {payment.daysInPeriod} {t("interestPayment.days")}
            </span>
          </div>

          <div className="flex justify-between items-center py-3">
            <span className="font-semibold text-gray-900 dark:text-white">
              {t("interestPayment.interestToPay")}:
            </span>
            <span className="font-bold text-lg text-green-600 dark:text-green-400">
              {formatCurrency(payment.interestAmount)}
            </span>
          </div>
        </div>

        {/* Formula Explanation */}
        <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-md border-l-4 border-blue-500">
          <p className="text-sm text-blue-800 dark:text-blue-300 font-mono leading-relaxed">
            {t("interestPayment.formula")}<br />
            <span className="mt-2 block">
              = {formatCurrency(payment.investmentAmount)} × {payment.interestRate}% × ({payment.daysInPeriod}/365)
            </span>
            <span className="mt-1 block font-semibold">
              = {formatCurrency(payment.interestAmount)}
            </span>
          </p>
        </div>
      </div>

      {/* Payment History Card */}
      <div className="bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-md">
            <Clock className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t("interestPayment.paymentHistory")}
          </h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                  {t("interestPayment.period")}
                </th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                  {t("interestPayment.paymentDate")}
                </th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                  {t("interestPayment.amount")}
                </th>
                <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                  {t("interestPayment.status")}
                </th>
              </tr>
            </thead>
            <tbody>
              {payment.paymentHistory.map((history, index) => (
                <tr
                  key={index}
                  className="border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                >
                  <td className="py-4 px-4 text-sm text-gray-900 dark:text-white">
                    {history.period}
                  </td>
                  <td className="py-4 px-4 text-sm text-gray-600 dark:text-gray-400">
                    {history.paymentDate}
                  </td>
                  <td className="py-4 px-4 text-sm text-right font-semibold text-green-600 dark:text-green-400">
                    {formatCurrency(history.amount)}
                  </td>
                  <td className="py-4 px-4 text-center">
                    {getSmallStatusBadge(history.status)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Documents Card */}
      <div className="bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-md">
            <FileText className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t("interestPayment.documents")}
          </h2>
        </div>

        <div className="space-y-3">
          {payment.documents.map((doc) => (
            <div
              key={doc.id}
              className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-md"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white dark:bg-gray-600 rounded-md">
                  <FileText className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {doc.fileName}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t("interestPayment.uploadedOn")}: {doc.uploadDate}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleDownload(doc.fileName)}
                className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 dark:text-primary transition-colors"
              >
                <Download className="w-4 h-4" />
                {t("interestPayment.download")}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Accounting Note Card */}
      <div className="bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 italic">
          {t("interestPayment.accountingNote")}
        </h2>
        <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-md">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            {payment.accountingNote}
          </p>
        </div>
      </div>
    </div>
  );
};

export default InterestPaymentDetail;
